import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.prenom || !formData.nom || !formData.email || !formData.telephone || !formData.message) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData.message.length > 500) {
      alert('Le message ne peut pas dépasser 500 caractères');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('prenom', formData.prenom);
      formBody.append('nom', formData.nom);
      formBody.append('email', formData.email);
      formBody.append('telephone', formData.telephone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4ac69r6dpbkhrueen40', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          prenom: '',
          nom: '',
          email: '',
          telephone: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#4A5240] mb-6"
              style={{ fontFamily: '"Cormorant Garamond", serif' }}
            >
              Contactez votre <strong>conciergerie à Perpignan</strong>
            </h2>
            <div className="w-24 h-1 bg-[#6B7C59] mx-auto mb-8"></div>
            <p 
              className="text-xl text-[#6B7C59] max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Vous cherchez une solution de <strong>gestion de location saisonnière dans les Pyrénées-Orientales</strong> ? Parlons de votre projet
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 
                className="text-2xl font-bold text-[#4A5240] mb-6"
                style={{ fontFamily: '"Cormorant Garamond", serif' }}
              >
                Nos coordonnées
              </h3>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#6B7C59]/10 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-[#6B7C59]"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#4A5240] mb-1" style={{ fontFamily: '"Montserrat", sans-serif' }}>Email</p>
                    <a href="mailto:contact@conciergerienova.com" className="text-[#6B7C59] hover:text-[#5A6B49] transition-colors cursor-pointer">
                      contact@conciergerienova.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#6B7C59]/10 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-[#6B7C59]"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#4A5240] mb-1" style={{ fontFamily: '"Montserrat", sans-serif' }}>Téléphone</p>
                    <a href="tel:+33650428625" className="text-[#6B7C59] hover:text-[#5A6B49] transition-colors cursor-pointer">
                      06 50 42 86 25
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#6B7C59]/10 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-[#6B7C59]"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#4A5240] mb-1" style={{ fontFamily: '"Montserrat", sans-serif' }}>Localisation</p>
                    <p className="text-[#6B7C59]">
                      Perpignan et Pyrénées-Orientales (66)
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#6B7C59]/10 rounded-lg flex-shrink-0">
                    <i className="ri-global-line text-2xl text-[#6B7C59]"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#4A5240] mb-1" style={{ fontFamily: '"Montserrat", sans-serif' }}>Site web</p>
                    <a href="https://www.conciergerienova.com" className="text-[#6B7C59] hover:text-[#5A6B49] transition-colors cursor-pointer">
                      www.conciergerienova.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block bg-stone-50 p-6 rounded-xl border border-stone-200">
                <div className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-2xl text-[#6B7C59] flex-shrink-0 mt-1"></i>
                  <div>
                    <p className="text-sm text-[#4A5240] leading-relaxed" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Vos données personnelles sont protégées conformément au RGPD. Elles seront uniquement utilisées pour vous recontacter concernant votre demande.
                    </p>
                    <a href="#" className="text-sm text-[#6B7C59] hover:text-[#5A6B49] underline mt-2 inline-block cursor-pointer">
                      Consulter notre charte de confidentialité
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="prenom" className="block text-sm font-semibold text-[#4A5240] mb-2" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Prénom *
                    </label>
                    <input
                      type="text"
                      id="prenom"
                      name="prenom"
                      value={formData.prenom}
                      onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B7C59] focus:border-transparent text-sm"
                      style={{ fontFamily: '"Montserrat", sans-serif' }}
                    />
                  </div>
                  <div>
                    <label htmlFor="nom" className="block text-sm font-semibold text-[#4A5240] mb-2" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Nom *
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={(e) => setFormData({...formData, nom: e.target.value})}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B7C59] focus:border-transparent text-sm"
                      style={{ fontFamily: '"Montserrat", sans-serif' }}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#4A5240] mb-2" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B7C59] focus:border-transparent text-sm"
                    style={{ fontFamily: '"Montserrat", sans-serif' }}
                  />
                </div>

                <div>
                  <label htmlFor="telephone" className="block text-sm font-semibold text-[#4A5240] mb-2" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    required
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B7C59] focus:border-transparent text-sm"
                    style={{ fontFamily: '"Montserrat", sans-serif' }}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#4A5240] mb-2" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                    Message * <span className="text-xs font-normal text-[#6B7C59]">(500 caractères maximum)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({...formData, message: e.target.value});
                      }
                    }}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B7C59] focus:border-transparent resize-none text-sm"
                    style={{ fontFamily: '"Montserrat", sans-serif' }}
                  ></textarea>
                  <p className="text-xs text-[#6B7C59] mt-1 text-right">
                    {formData.message.length}/500 caractères
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-[#6B7C59] text-white rounded-lg hover:bg-[#5A6B49] transition-all shadow-lg hover:shadow-xl font-semibold text-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  style={{ fontFamily: '"Montserrat", sans-serif' }}
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-3">
                    <i className="ri-checkbox-circle-line text-2xl"></i>
                    <p className="text-sm" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Merci pour votre message ! Nous vous recontacterons très rapidement.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-3">
                    <i className="ri-error-warning-line text-2xl"></i>
                    <p className="text-sm" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Une erreur est survenue. Veuillez réessayer ou nous contacter directement.
                    </p>
                  </div>
                )}
              </form>

              <div className="lg:hidden bg-stone-50 p-6 rounded-xl border border-stone-200 mt-6">
                <div className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-2xl text-[#6B7C59] flex-shrink-0 mt-1"></i>
                  <div>
                    <p className="text-sm text-[#4A5240] leading-relaxed" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                      Vos données personnelles sont protégées conformément au RGPD. Elles seront uniquement utilisées pour vous recontacter concernant votre demande.
                    </p>
                    <a href="#" className="text-sm text-[#6B7C59] hover:text-[#5A6B49] underline mt-2 inline-block cursor-pointer">
                      Consulter notre charte de confidentialité
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
